import { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { clearMatchingSession, readSavedSession } from '../utils/authSession';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on page load
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    const checkLoggedIn = async () => {
      const saved = readSavedSession();
      if (saved) {
        try {
          await axios.get('/api/users/profile', {
            headers: { Authorization: `Bearer ${saved.token}` }, signal: controller.signal,
          });
          if (!cancelled) setUser(saved);
        } catch (error) {
          if (error.response?.status === 401) clearMatchingSession(saved.token);
          // A network outage must not erase a valid saved session.
          else if (!cancelled) setUser(saved);
        }
      }
      clearTimeout(timer);
      if (!cancelled) setLoading(false);
    };

    checkLoggedIn();
    const interceptor = axios.interceptors.response.use(response => response, error => {
      const header = error.config?.headers?.Authorization || error.config?.headers?.authorization;
      const url = new URL(error.config?.url || '/', window.location.origin);
      if (error.response?.status === 401 && url.origin === window.location.origin && url.pathname.startsWith('/api/')
          && typeof header === 'string' && header.startsWith('Bearer ')
          && clearMatchingSession(header.slice(7))) {
        setUser(null);
      }
      return Promise.reject(error);
    });
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); axios.interceptors.response.eject(interceptor); };
  }, []);

  // Login user
  const login = async (username, password) => {
    try {
      
      // Make sure we're sending the data in the correct format
      const requestData = {
        username: username.trim(),
        password: password.trim()
      };
      
      const { data } = await axios.post('/api/users/login', requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Store user info in localStorage and state
      localStorage.setItem('userInfo', JSON.stringify(data));
      setUser(data);
      return { success: true };
    } catch (error) {
      
      return {
        success: false,
        message: error.response?.data?.message || 'Invalid credentials. Please check your username and password.',
      };
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('userInfo');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
